<template>
  <div>
    <StackedHorizontalBarChart
      :title="chartTitle"
      :filterContext="filterDocumentsContext"
      :dataProviders="dataProviders"
      :contextUpdaters="contextUpdaters"
      :navigationConfig="navigationConfig"
      :chartFontColor="chartFontColor"
      chartCardHeight="400"
      @updateFilter="handleFilterUpdate"
    />
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue';
  import { useStore } from 'vuex';
  import { useI18n } from 'vue-i18n';
  import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';
  
  const props = defineProps({
    filterDocumentsContext: {
      type: Object,
      required: true
    }
  });

  const store = useStore();
  const { t } = useI18n();
  const chartFontColor = computed(() => store.getters.chartFontColor);
  const conn = store.getters.getDBConnection;

  const chartName = 'teamUserDocumentBarChart';
  const chartTitle = t('documentsByTeamAndUser');

  const selectedTeamId = ref(null);
  const selectedUserId = ref(null);
  
  const getTeams = async () => {
    const query = `
      SELECT 
          COALESCE(t.id, 'unassigned') AS id,
          COALESCE(t.name, 'Un-assigned') AS title,
          'Document' AS status_title,
          '#5470c6' AS color,
          COUNT(*) AS value,
          1 AS sort
      FROM filteredDocuments d
      LEFT JOIN teams t ON d.createdByTeamId = t.id
      GROUP BY COALESCE(t.id, 'unassigned'), COALESCE(t.name, 'Un-assigned')
      ORDER BY COALESCE(t.name, 'Un-assigned')
    `;
    
    return executeAndProcessQuery(query);
  };
     
  const getUsers = async () => {
    const query = `
      SELECT 
          COALESCE(u.id, 'unassigned') AS id,
          COALESCE(u.displayName, 'Un-assigned') AS title,
          'Document' AS status_title,
          '#91cc75' AS color,
          COUNT(*) AS value,
          1 AS sort
      FROM filteredDocuments d
      JOIN teams t ON d.createdByTeamId = t.id
      LEFT JOIN users u ON d.createdByUserId = u.id
      WHERE t.id = '${selectedTeamId.value === 'unassigned' ? '' : selectedTeamId.value}'
      GROUP BY COALESCE(u.id, 'unassigned'), COALESCE(u.displayName, 'Un-assigned')
      ORDER BY COALESCE(u.displayName, 'Un-assigned')
    `;
    
    return executeAndProcessQuery(query);
  };
  
  const getSingleUser = async () => {
    const query = `
      SELECT 
          COALESCE(u.id, 'unassigned') AS id,
          COALESCE(u.displayName, 'Un-assigned') AS title,
          'Document' AS status_title,
          '#91cc75' AS color,
          COUNT(*) AS value,
          1 AS sort
      FROM filteredDocuments d
      JOIN teams t ON d.createdByTeamId = t.id
      LEFT JOIN users u ON d.createdByUserId = u.id
      WHERE t.id = '${selectedTeamId.value === 'unassigned' ? '' : selectedTeamId.value}'
      AND u.id = '${selectedUserId.value === 'unassigned' ? '' : selectedUserId.value}'
      GROUP BY COALESCE(u.id, 'unassigned'), COALESCE(u.displayName, 'Un-assigned')
      ORDER BY COALESCE(u.displayName, 'Un-assigned')
    `;
    
    return executeAndProcessQuery(query);
  };
  
  const getTeamFilterContext = async (teamId) => {
    selectedTeamId.value = teamId;

    if (teamId == 'unassigned') {
      const query = `
        SELECT DISTINCT d.id
        FROM documents d
        WHERE d.createdByTeamId = ''
      `;
      await updateFilteredItemsContext(query);
    } else {
      const query = `
        SELECT DISTINCT d.id
        FROM documents d
        WHERE d.createdByTeamId = '${teamId}'
      `;
      await updateFilteredItemsContext(query);
    }
  };

  const getUserFilterContext = async (userId) => {
    selectedUserId.value = userId;
    
    if (userId == 'unassigned') {
      const query = `
        SELECT DISTINCT d.id
        FROM documents d
        WHERE d.createdByTeamId = '${selectedTeamId.value === 'unassigned' ? '' : selectedTeamId.value}' 
        AND d.createdByUserId = ''
      `;
      await updateFilteredItemsContext(query);
    } else {
      const query = `
        SELECT DISTINCT d.id
        FROM documents d
        WHERE d.createdByTeamId = '${selectedTeamId.value}' 
        AND d.createdByUserId = '${userId}'
      `;
      await updateFilteredItemsContext(query);
    }
  };

  const resetFilterContext = async () => {
    selectedTeamId.value = null;
    selectedUserId.value = null;
    await props.filterDocumentsContext.removeFilter(chartName);
  };

  const dataProviders = {
    teams: getTeams,
    users: getUsers,
    singleuser: getSingleUser
  };
  
  const contextUpdaters = {
    teams: getTeamFilterContext,
    users: getUserFilterContext,
    singleuser: getUserFilterContext,
    reset: resetFilterContext
  };
  
  const navigationConfig = [
    { level: 'teams', titleKey: 'teams' },
    { level: 'users', titleKey: 'users' },
    { level: 'singleuser', titleKey: 'singleuser' }
  ];

  const updateFilteredItemsContext = async (query) => {
    await props.filterDocumentsContext.updateFilterWithQuery(chartName, query);
  };

  // Shared data processing utility function
  const processChartData = (rows) => {
    const groupedData = {};
    
    rows.forEach(row => {
      const id = row['id'];
      const title = row['title'];
      const parentId = row['parentId'] ? row['parentId'] : null;
      const type = row['type'] ? row['type'] : null;
      
      if (!groupedData[id]) {
        groupedData[id] = {
          name: title,
          id: id,
          parentId: parentId,
          type: type,
          children: []
        };
      }
      
      // Find if this color already exists in the children
      const existingColor = groupedData[id].children.find(
        child => child.color === row.color
      );
      
      if (existingColor) {
        existingColor.value += row.value;
        // Store the status names by color
        if (!existingColor.statusCounts) {
          existingColor.statusCounts = {};
        }
        existingColor.statusCounts[row.status_title] = row.value;
      } else {
        groupedData[id].children.push({
          color: row.color,
          value: row.value,
          statusName: row.status_title,
          sort: row.sort,
          statusCounts: { [row.status_title]: row.value }
        });
      }
    });
    
    return Object.values(groupedData).map(item => ({
      ...item,
      children: item.children.sort((a, b) => a.sort - b.sort)
    }));
  };

  // Generic query execution function
  const executeAndProcessQuery = async (query, idField, titleField) => {
    const result = await conn.query(query);
    const rows = result.toArray().map((row) => {
      const jsonRow = row.toJSON();
      return {
        ...jsonRow,
        value: Number(jsonRow.value),
        sort: Number(jsonRow.sort)
      };
    });
    
    return processChartData(rows, idField, titleField);
  };

  </script>
